/**
 * Client-side data access for the IM (Information Memorandum) routes. Kept
 * separate from the views so W5.2's full table + inline-edit can reuse the same
 * fetch/upload helpers introduced here for W4.3's drag-and-drop zone.
 *
 * Over JSON the server (server/src/db/ims.ts) serialises NUMERIC columns as
 * *strings* (precision-preserving) and TIMESTAMPTZ as ISO strings — this Im
 * shape reflects that, and views format the strings for display.
 */

/**
 * The deal pipeline, in board order. `stage` is the server's source of truth;
 * the first_call_requested / first_call_scheduled booleans are derived from it
 * (see server/src/db/ims.ts), so moving a card and flipping a toggle agree.
 */
export const STAGES = [
  'rejected_by_us',
  'teaser_received',
  'want_im',
  'im_received',
  'want_to_talk',
  'call_scheduled',
  'call_had',
  'offer_made',
  'rejected_by_them',
] as const;
export type Stage = (typeof STAGES)[number];

export const STAGE_LABELS: Record<Stage, string> = {
  rejected_by_us: 'Rejected by us',
  teaser_received: 'Teaser received',
  want_im: 'Want to see IM',
  im_received: 'IM received',
  want_to_talk: 'Want to talk',
  call_scheduled: 'Call scheduled',
  call_had: 'Call had',
  offer_made: 'Offer made',
  rejected_by_them: 'Rejected by them',
};

/** The dead ends at each end of the board — a deal here is out of the running. */
export const REJECTION_STAGES: readonly Stage[] = ['rejected_by_us', 'rejected_by_them'];

export function isRejectedStage(stage: Stage): boolean {
  return REJECTION_STAGES.includes(stage);
}

/** One message in a deal's correspondence, as the server logged it. */
export interface DealEmail {
  id: string;
  im_id: string;
  gmail_message_id: string | null;
  gmail_thread_id: string | null;
  direction: 'inbound' | 'outbound';
  kind: string | null;
  from_addr: string | null;
  to_addrs: string | null;
  subject: string | null;
  body_text: string | null;
  sent_at: string | null;
  by_agent: boolean;
  created_at: string;
}

export interface Im {
  id: string;
  opportunity_number: number;
  source: 'upload' | 'email';
  email_message_id: string | null;
  gcs_object: string;
  original_filename: string | null;
  content_hash: string | null;
  teaser_gcs_object: string | null;
  teaser_original_filename: string | null;
  broker_reference: string | null;
  name: string | null;
  founder: string | null;
  sellers: string | null;
  initial_rating: string | null;
  rating_detail: Record<string, unknown> | null;
  revenue: string | null;
  ebitda: string | null;
  asking_for: string | null;
  num_clients: number | null;
  tech_stack: string | null;
  stage: Stage;
  stage_changed_at: string;
  document_kind: 'teaser' | 'im';
  gmail_thread_id: string | null;
  counterparty_email: string | null;
  counterparty_name: string | null;
  company_number: string | null;
  agent_enabled: boolean;
  agent_paused_reason: string | null;
  call_scheduled_at: string | null;
  /** Who is working the deal; null while it's unclaimed. */
  owner_email: string | null;
  claimed_at: string | null;
  still_in_running: boolean;
  rejected_at: string | null;
  first_call_requested: boolean;
  first_call_scheduled: boolean;
  offered: string | null;
  knightsbridge_contact: string | null;
  notes: string | null;
  outcome: string | null;
  rate_error: string | null;
  rated_at: string | null;
  created_at: string;
  updated_at: string;
}

/**
 * Thrown when an /api call returns 401/403 mid-session — the server is the auth
 * source of truth, so the caller should bounce back through the auth gate
 * (reload) rather than render stale UI.
 */
export class AuthExpiredError extends Error {
  constructor() {
    super('Session expired');
    this.name = 'AuthExpiredError';
  }
}

/**
 * Thrown when the server says this person may not do that (a deal someone
 * else owns, an admin-only action). Unlike AuthExpiredError the session is
 * fine, so the caller shows the message rather than reloading.
 */
export class NotAllowedError extends Error {
  constructor(message: string) {
    super(message);
    this.name = 'NotAllowedError';
  }
}

const NOT_ALLOWED_MESSAGES: Record<string, string> = {
  not_owner: "Only the deal's owner (or an admin) can change it. Claim it first.",
  admin_only: 'Only an admin can do that.',
  not_author: 'You can only delete your own notes.',
  viewing_as: "You're viewing as a teammate, which is read-only. Go back to your own view to make changes.",
};

/**
 * The auth gate answers 401/403 with an empty body (session gone or not
 * allowed in at all); a route refusing one action answers 403 with a JSON
 * error. Only the first means the session has expired.
 */
async function throwIfDenied(res: Response): Promise<void> {
  if (res.status === 401) throw new AuthExpiredError();
  if (res.status !== 403) return;
  const text = await res.text();
  if (!text) throw new AuthExpiredError();
  let code = '';
  try {
    code = (JSON.parse(text) as { error?: string }).error ?? '';
  } catch {
    /* not JSON */
  }
  throw new NotAllowedError(NOT_ALLOWED_MESSAGES[code] ?? "You can't do that.");
}

export async function listIms(): Promise<Im[]> {
  const res = await fetch('/api/ims');
  if (res.status === 401 || res.status === 403) throw new AuthExpiredError();
  if (!res.ok) throw new Error(`Couldn't load deals (${res.status})`);
  return (await res.json()) as Im[];
}

// A "not found" signal the detail view turns into a friendly empty state.
export class ImNotFoundError extends Error {
  constructor() {
    super('Deal not found');
    this.name = 'ImNotFoundError';
  }
}

export async function getIm(id: string): Promise<Im> {
  const res = await fetch(`/api/ims/${encodeURIComponent(id)}`);
  if (res.status === 401 || res.status === 403) throw new AuthExpiredError();
  if (res.status === 404) throw new ImNotFoundError();
  if (!res.ok) throw new Error(`Couldn't load deal (${res.status})`);
  return (await res.json()) as Im;
}

/**
 * The fields the list view can PATCH — the manual workflow fields plus the
 * AI-derived fields the user may override by hand. Mirrors the server's
 * UpdateImInput whitelist (server/src/db/ims.ts). NUMERIC columns are sent as
 * numbers (or null to clear); the server normalises them back to strings.
 * `still_in_running` drives `rejected_at` server-side (off ⇒ stamp, on ⇒ clear).
 */
export interface PatchImFields {
  stage?: Stage;
  counterparty_email?: string | null;
  counterparty_name?: string | null;
  company_number?: string | null;
  agent_enabled?: boolean;
  agent_paused_reason?: string | null;
  still_in_running?: boolean;
  first_call_requested?: boolean;
  first_call_scheduled?: boolean;
  offered?: number | null;
  knightsbridge_contact?: string | null;
  notes?: string | null;
  outcome?: string | null;
  broker_reference?: string | null;
  name?: string | null;
  founder?: string | null;
  sellers?: string | null;
  initial_rating?: number | null;
  revenue?: number | null;
  ebitda?: number | null;
  asking_for?: number | null;
  num_clients?: number | null;
  tech_stack?: string | null;
}

export async function patchIm(id: string, fields: PatchImFields): Promise<Im> {
  const res = await fetch(`/api/ims/${encodeURIComponent(id)}`, {
    method: 'PATCH',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(fields),
  });
  await throwIfDenied(res);
  if (!res.ok) throw new Error(`Couldn't save change (${res.status})`);
  return (await res.json()) as Im;
}

/** Thrown when someone else claimed the deal first; carries the deal as it is now. */
export class AlreadyClaimedError extends Error {
  constructor(public readonly im: Im | null) {
    super('Someone else has already claimed this deal.');
    this.name = 'AlreadyClaimedError';
  }
}

async function ownership(id: string, action: string, body?: unknown): Promise<Im> {
  const res = await fetch(`/api/ims/${encodeURIComponent(id)}/${action}`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: body === undefined ? undefined : JSON.stringify(body),
  });
  await throwIfDenied(res);
  if (res.status === 409) {
    const payload = (await res.json().catch(() => ({}))) as { im?: Im };
    throw new AlreadyClaimedError(payload.im ?? null);
  }
  if (!res.ok) throw new Error(`Couldn't ${action} the deal (${res.status})`);
  return (await res.json()) as Im;
}

/** Take an unclaimed deal. */
export function claimIm(id: string): Promise<Im> {
  return ownership(id, 'claim');
}

/** Hand a deal back to the unclaimed pool (its owner, or an admin). */
export function releaseIm(id: string): Promise<Im> {
  return ownership(id, 'release');
}

/** Admin: give a deal to someone, or null to release it. */
export function assignIm(id: string, ownerEmail: string | null): Promise<Im> {
  return ownership(id, 'assign', { owner_email: ownerEmail });
}

// Delete a deal (row + stored PDF). 204 on success; 404 is treated as success
// (already gone — the row is the source of truth either way).
/** One dated note somebody left on a deal. */
export interface DealNote {
  id: string;
  im_id: string;
  body: string;
  author: string | null;
  created_at: string;
}

/** A deal's notes, newest first. */
export async function listDealNotes(id: string): Promise<DealNote[]> {
  const res = await fetch(`/api/ims/${encodeURIComponent(id)}/notes`);
  if (res.status === 401 || res.status === 403) throw new AuthExpiredError();
  if (res.status === 404) throw new ImNotFoundError();
  if (!res.ok) throw new Error(`Couldn't load the notes (${res.status})`);
  return (await res.json()) as DealNote[];
}

export async function addDealNote(id: string, body: string): Promise<DealNote> {
  const res = await fetch(`/api/ims/${encodeURIComponent(id)}/notes`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ body }),
  });
  await throwIfDenied(res);
  if (!res.ok) throw new Error(`Couldn't save the note (${res.status})`);
  return (await res.json()) as DealNote;
}

export async function deleteDealNote(id: string, noteId: string): Promise<void> {
  const res = await fetch(
    `/api/ims/${encodeURIComponent(id)}/notes/${encodeURIComponent(noteId)}`,
    { method: 'DELETE' },
  );
  await throwIfDenied(res);
  if (!res.ok && res.status !== 404) throw new Error(`Couldn't delete the note (${res.status})`);
}

/** What a file or link on a deal is, for grouping and the icon. */
export const DEAL_FILE_CATEGORIES = ['document', 'nda', 'financials', 'model', 'other'] as const;
export type DealFileCategory = (typeof DEAL_FILE_CATEGORIES)[number];

export const DEAL_FILE_CATEGORY_LABELS: Record<DealFileCategory, string> = {
  document: 'Document',
  nda: 'NDA',
  financials: 'Financials',
  model: 'Cashflow model',
  other: 'Other',
};

/** An extra file (in the private bucket) or a link on a deal. */
export interface DealFile {
  id: string;
  im_id: string;
  kind: 'file' | 'link';
  category: DealFileCategory;
  label: string;
  original_filename: string | null;
  content_type: string | null;
  size_bytes: string | null;
  url: string | null;
  added_by: string | null;
  created_at: string;
}

/** The file types the server takes (it checks again). */
export const DEAL_FILE_ACCEPT =
  '.pdf,.xlsx,.xlsm,.xls,.csv,.ods,.numbers,.docx,.doc,.odt,.pptx,.ppt,.txt,.md,.png,.jpg,.jpeg,.webp,.zip,.eml,.msg';

const DEAL_FILE_ERRORS: Record<string, string> = {
  unsupported_type: "That type of file isn't supported.",
  not_a_pdf: "That file says it's a PDF but isn't one.",
  file_too_large: 'That file is too large (25 MB at most).',
  too_many_files: 'At most 10 files at a time.',
  no_file: 'No file was selected.',
  invalid_url: 'That link needs to start with https://.',
};

async function dealFileError(res: Response, fallback: string): Promise<Error> {
  let code = '';
  let filename = '';
  try {
    const body = (await res.json()) as { error?: string; filename?: string };
    code = body.error ?? '';
    filename = body.filename ?? '';
  } catch {
    /* not JSON */
  }
  const message = DEAL_FILE_ERRORS[code] ?? `${fallback} (${res.status})`;
  return new Error(filename ? `${filename}: ${message}` : message);
}

export async function listDealFiles(id: string): Promise<DealFile[]> {
  const res = await fetch(`/api/ims/${encodeURIComponent(id)}/files`);
  if (res.status === 401 || res.status === 403) throw new AuthExpiredError();
  if (res.status === 404) throw new ImNotFoundError();
  if (!res.ok) throw new Error(`Couldn't load the files (${res.status})`);
  return (await res.json()) as DealFile[];
}

/**
 * Upload files to a deal in one request, reporting progress. Rejects with a
 * readable message (the server refuses the whole batch if one file is bad).
 */
export function uploadDealFiles(
  id: string,
  files: File[],
  category: DealFileCategory,
  onProgress?: (fraction: number) => void,
): Promise<DealFile[]> {
  return new Promise((resolve, reject) => {
    const form = new FormData();
    form.append('category', category);
    for (const f of files) form.append('file', f);
    const xhr = new XMLHttpRequest();
    xhr.open('POST', `/api/ims/${encodeURIComponent(id)}/files`);
    if (onProgress && xhr.upload) {
      xhr.upload.onprogress = (e) => {
        if (e.lengthComputable) onProgress(e.loaded / e.total);
      };
    }
    xhr.onload = () => {
      let body: Record<string, unknown> = {};
      try {
        body = JSON.parse(xhr.responseText) as Record<string, unknown>;
      } catch {
        /* not JSON */
      }
      if (xhr.status === 201) {
        resolve(body as unknown as DealFile[]);
      } else if (xhr.status === 401 || (xhr.status === 403 && !xhr.responseText)) {
        reject(new AuthExpiredError());
      } else if (xhr.status === 403) {
        reject(new NotAllowedError(NOT_ALLOWED_MESSAGES[String(body.error)] ?? "You can't do that."));
      } else {
        const message = DEAL_FILE_ERRORS[String(body.error)] ?? `Upload failed (${xhr.status}).`;
        reject(new Error(body.filename ? `${String(body.filename)}: ${message}` : message));
      }
    };
    xhr.onerror = () => reject(new Error('Network error during upload.'));
    xhr.send(form);
  });
}

export async function addDealLink(
  id: string,
  link: { url: string; label?: string; category?: DealFileCategory },
): Promise<DealFile> {
  const res = await fetch(`/api/ims/${encodeURIComponent(id)}/links`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(link),
  });
  await throwIfDenied(res);
  if (!res.ok) throw await dealFileError(res, "Couldn't add the link");
  return (await res.json()) as DealFile;
}

export async function deleteDealFile(id: string, fileId: string): Promise<void> {
  const res = await fetch(
    `/api/ims/${encodeURIComponent(id)}/files/${encodeURIComponent(fileId)}`,
    { method: 'DELETE' },
  );
  await throwIfDenied(res);
  if (!res.ok && res.status !== 404) throw new Error(`Couldn't delete the file (${res.status})`);
}

/**
 * A JPEG of the first page of the deal's main document (or of the teaser kept
 * beside it). `version` changes when the document does, so the browser's
 * cached copy of the old one isn't shown.
 */
export function imThumbnailHref(id: string, version: string, teaser = false): string {
  const v = encodeURIComponent(version.split('/').pop() ?? version);
  return `/api/ims/${encodeURIComponent(id)}/thumbnail?${teaser ? 'doc=teaser&' : ''}v=${v}`;
}

/** A JPEG of an uploaded PDF's first page, or of an image; 404 for other types. */
export function dealFileThumbnailHref(id: string, fileId: string): string {
  return `/api/ims/${encodeURIComponent(id)}/files/${encodeURIComponent(fileId)}/thumbnail`;
}

/** Opens a file (a short-lived signed URL behind the auth gate) or follows a link. */
export function dealFileHref(id: string, fileId: string, inline = false): string {
  return `/api/ims/${encodeURIComponent(id)}/files/${encodeURIComponent(fileId)}/download${inline ? '?inline=1' : ''}`;
}

/** The correspondence behind a deal, oldest first. */
export async function listDealEmails(id: string): Promise<DealEmail[]> {
  const res = await fetch(`/api/ims/${encodeURIComponent(id)}/emails`);
  if (res.status === 401 || res.status === 403) throw new AuthExpiredError();
  if (res.status === 404) throw new ImNotFoundError();
  if (!res.ok) throw new Error(`Couldn't load the email history (${res.status})`);
  return (await res.json()) as DealEmail[];
}

export async function deleteIm(id: string): Promise<void> {
  const res = await fetch(`/api/ims/${encodeURIComponent(id)}`, { method: 'DELETE' });
  await throwIfDenied(res);
  if (!res.ok && res.status !== 404) throw new Error(`Couldn't delete deal (${res.status})`);
}

/**
 * Hrefs for the auth-gated preview/download endpoints (W6.1). The browser hits
 * these directly (as an <a href> or <iframe src>), sending the session cookie
 * automatically — NOT via fetch, so there's no AuthExpiredError path here. A
 * lapsed session just renders the server's 401 inside the iframe/tab. These
 * always point at the auth-gated route; never a raw bucket or durable URL.
 */
export function imDownloadHref(id: string): string {
  return `/api/ims/${encodeURIComponent(id)}/download`;
}

export function imPreviewHref(id: string): string {
  return `/api/ims/${encodeURIComponent(id)}/preview`;
}

/** The teaser kept on a deal whose main document is now its IM. */
export function teaserPreviewHref(id: string): string {
  return `/api/ims/${encodeURIComponent(id)}/preview?doc=teaser`;
}

/**
 * Generate the AI-voiced audio briefing of a deal (W11) and return the URL to
 * play it from — a short-lived signed GCS URL in prod, a local streaming route
 * in dev. The audio bytes never travel through this JSON response (the prod
 * Firebase Hosting rewrite stalls large bodies). Slow by design (the server
 * runs an LLM + text-to-speech pass per call), so callers show a loading state
 * and keep the audio element for replay.
 */
export async function fetchImNarration(id: string): Promise<string> {
  const res = await fetch(`/api/ims/${encodeURIComponent(id)}/narration`);
  if (res.status === 401 || res.status === 403) throw new AuthExpiredError();
  if (!res.ok) throw new Error(`Couldn't generate the audio briefing (${res.status})`);
  const body = (await res.json()) as { url?: unknown };
  if (typeof body.url !== 'string' || body.url === '') {
    throw new Error('Audio briefing response carried no URL.');
  }
  return body.url;
}

// 400-family error codes the upload route returns, mapped to user-facing copy.
const UPLOAD_REJECTION_COPY: Record<string, string> = {
  not_a_pdf: "That file isn't a PDF.",
  file_too_large: 'That file is too large.',
  no_file: 'No file was selected.',
  upload_failed: 'Upload failed — please try again.',
};

export type UploadOutcome =
  | { kind: 'created'; im: Im }
  | { kind: 'duplicate'; id: string }
  | { kind: 'rejected'; message: string } // 400 family — bad/oversize file
  | { kind: 'auth' } // 401/403 — session lapsed
  | { kind: 'error'; message: string }; // 500 / network

/**
 * POST a PDF to /api/ims as multipart (field name `file`, matching the route).
 * Uses XMLHttpRequest rather than fetch so we can report upload progress.
 * Never rejects — every outcome (including network failure) resolves to a
 * tagged UploadOutcome the caller can switch on.
 */
export function uploadIm(
  file: File,
  onProgress?: (fraction: number) => void,
): Promise<UploadOutcome> {
  return new Promise((resolve) => {
    const form = new FormData();
    form.append('file', file);

    const xhr = new XMLHttpRequest();
    xhr.open('POST', '/api/ims');
    if (onProgress && xhr.upload) {
      xhr.upload.onprogress = (e) => {
        if (e.lengthComputable) onProgress(e.loaded / e.total);
      };
    }
    xhr.onload = () => {
      let body: unknown = null;
      try {
        body = JSON.parse(xhr.responseText);
      } catch {
        body = null;
      }
      const record = (body && typeof body === 'object' ? body : {}) as Record<string, unknown>;

      if (xhr.status === 201) {
        resolve({ kind: 'created', im: body as Im });
      } else if (xhr.status === 409) {
        resolve({ kind: 'duplicate', id: String(record.id ?? '') });
      } else if (xhr.status === 400) {
        const code = typeof record.error === 'string' ? record.error : '';
        resolve({ kind: 'rejected', message: UPLOAD_REJECTION_COPY[code] ?? 'That file was rejected.' });
      } else if (xhr.status === 401 || xhr.status === 403) {
        resolve({ kind: 'auth' });
      } else {
        resolve({ kind: 'error', message: `Upload failed (${xhr.status}).` });
      }
    };
    xhr.onerror = () => resolve({ kind: 'error', message: 'Network error during upload.' });
    xhr.send(form);
  });
}
